import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { API_BASE_URL } from '../../utils/apiClient';
import './JSDos.css';

const JSDos = ({ game, selectedRomVersion }) => {
  const iframeRef = useRef(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [emulatorReady, setEmulatorReady] = useState(false);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Use the ROM path already selected in the game object
  const romToUse = game.rom;

  // Parse launch options from game object
  const parseLaunchOptions = () => {
    const launchOptions = game.launchoptions || '';
    console.log('JSDos: Processing launch options:', launchOptions);
    
    // Check if netplay is enabled
    const netplayMatch = launchOptions.match(/--netplay=(true|false)/);
    const isNetplayEnabled = netplayMatch && netplayMatch[1] === 'true';
    
    if (isNetplayEnabled) {
      console.log('JSDos: Netplay is enabled in launch options');
    }
    
    return { 
      isNetplayEnabled
    };
  };
  
  // Get parsed launch options
  const { isNetplayEnabled } = parseLaunchOptions();

  // Function to send commands to the emulator iframe
  const sendCommand = useCallback((command, params = {}) => {
    if (!iframeRef.current || !emulatorReady) {
      console.warn('Cannot send command, emulator not ready:', command);
      return false;
    }
    
    try {
      iframeRef.current.contentWindow.postMessage({
        type: 'command',
        command: command,
        params: params
      }, '*');
      return true;
    } catch (e) {
      console.error('Error sending command to emulator:', e);
      return false;
    }
  }, [emulatorReady]);

  // API methods to control the emulator
  const emulatorApi = {
    pause: () => sendCommand('pause'),
    resume: () => sendCommand('resume'),
    reset: () => sendCommand('reset'),
    toggleFullscreen: () => sendCommand('fullscreen')
  };

  // Reset state when ROM changes
  useEffect(() => {
    setIframeLoaded(false);
    setEmulatorReady(false);
    setError(null);
  }, [romToUse, isNetplayEnabled]);

  // Handle messages from the iframe
  useEffect(() => {
    const handleMessage = (event) => {
      // Make sure the message is from our iframe
      if (!event.data || event.data.source !== 'js-dos-emulator') return;
      
      const { type, data } = event.data;
      
      switch (type) {
        case 'ready':
          console.log('Emulator reported ready:', data);
          setEmulatorReady(true);
          break;
          
        case 'error':
          console.error('Emulator reported error:', data);
          setError(`Error: ${data.error || 'Unknown error from emulator'}`);
          break;
          
        case 'fullscreenChange':
          setIsFullscreen(data.isFullscreen);
          break;
          
        default:
          console.log('Message from emulator:', type, data);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleIframeLoad = () => {
    setIframeLoaded(true);
    console.log('JSDos iframe loaded for ROM:', romToUse);
  };

  const handleIframeError = () => {
    setError('Failed to load the emulator. Please try again later.');
    console.error('JSDos iframe failed to load');
  };

  // Construct the iframe source URL with the ROM path and netplay parameter if enabled
  const iframeSrc = `/js-dos-wrapper.html?rom=${encodeURIComponent(`${API_BASE_URL}${romToUse}`)}&netplay=${isNetplayEnabled}`;

  return (
    <div className={`js-dos-container ${isFullscreen ? 'is-fullscreen' : ''}`} data-rom={romToUse}>
      {error ? (
        <div className="js-dos-error">{error}</div>
      ) : (
        <>
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            className="js-dos-iframe"
            title="JS-DOS Emulator"
            allow="fullscreen; autoplay; clipboard-read; clipboard-write"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            frameBorder="0"
          />
          
          {emulatorReady && (
            <div className="js-dos-controls">
              <button 
                onClick={emulatorApi.toggleFullscreen} 
                className="js-dos-control-btn"
                title="Toggle Fullscreen"
              >
                {isFullscreen ? '⤦' : '⤢'}
              </button>
              <button 
                onClick={emulatorApi.reset} 
                className="js-dos-control-btn" 
                title="Reset Game"
              >
                ↺
              </button>
            </div>
          )}
        </>
      )}
      {!iframeLoaded && !error && (
        <div className="js-dos-loading">Loading emulator...</div>
      )}
    </div>
  );
};

JSDos.propTypes = {
  game: PropTypes.object.isRequired,
  selectedRomVersion: PropTypes.string.isRequired
};

export default JSDos; 